/**
 * Coordinates the animations that make up one base drawer transition. It owns
 * handler cleanup and guarantees that every interrupted transition settles.
 */
export default class SlidingContainerTransition {
    constructor() {
        this.finish = null;
    }

    /**
     * Immediately settles the active transition, if any.
     */
    finishImmediately() {
        this.finish?.();
    }

    /**
     * Runs a group of animations as one transition.
     * @param {object} options Complete definition of the coordinated animation group.
     * @param {Animation} options.primary Animation whose finish settles the group.
     * @param {Animation[]} options.animations All animations owned by the group.
     * @param {() => void} options.cleanup Additional owned-resource cleanup.
     * @param {() => void} options.settle Applies the final component state.
     * @returns {Promise<void>} Resolves exactly once after settling.
     */
    run({ primary, animations, cleanup, settle }) {
        return new Promise((resolve) => {
            let isFinished = false;
            const finish = () => {
                if (isFinished) return;
                isFinished = true;

                if (this.finish === finish) this.finish = null;
                for (const animation of animations) {
                    animation.onfinish = null;
                    animation.oncancel = null;
                }

                cleanup?.();
                for (const animation of animations) animation.cancel();
                settle();
                resolve();
            };

            this.finish = finish;
            primary.onfinish = finish;
            for (const animation of animations) animation.oncancel = finish;
        });
    }

    /**
     * Drops the active callback after its resources were cleaned elsewhere.
     */
    reset() {
        this.finish = null;
    }
}
